/**
 * Module Grid — Grid Generation and Polarity Detection
 *
 * THE single geometry authority for module cells (2026-09-02 consolidation:
 * calculateModuleNumber — zero callers, disagreed with this path, ignored
 * grid offsets — is deleted; useIVOverlay's string rings consume
 * stringGridFrame instead of hand-mirroring the math).
 *
 * Pure functions for:
 * 1. Resolving a string's effective grid parameters (stringGridFrame —
 *    site defaults + sparse per-string overrides, REPLACE semantics; see
 *    the StringOverride doc block in domain/layout/types.ts)
 * 2. Generating GeoJSON module rectangles — whole site or scoped
 * 3. Auto-detecting polarity from PDF "+" and "-" text items
 */

import type {
  SiteLayout,
  LayoutTextEntity,
  StringOverride,
} from '../layout/types';
import { transformPoint } from '../layout/transform';
import { effectiveClassification } from '../layout/classify';
import { metersToDeg } from '../../utils/geometry';

// ── Local meter projection ───────────────────────────────

function toLocalMeters(
  lng: number,
  lat: number,
  originLng: number,
  originLat: number,
): { x: number; y: number } {
  const mpdLat = 111320;
  const mpdLng = 111320 * Math.cos((originLat * Math.PI) / 180);
  return {
    x: (lng - originLng) * mpdLng,
    y: (lat - originLat) * mpdLat,
  };
}

// ── Effective per-string grid frame ──────────────────────

/** A string's fully-resolved grid parameters — the ONE place site defaults
 *  and per-string overrides combine. Everything that draws or reasons about
 *  a string's modules starts from this. */
export interface StringGridFrame {
  entity: LayoutTextEntity;
  /** Grid origin (label position + along/perp offsets), lng/lat */
  gridLng: number;
  gridLat: number;
  /** Effective axis direction in degrees (rotation/override + site offset) */
  directionDeg: number;
  moduleCount: number;
  rows: number;
  modulesPerRow: number;
  w: number;
  g: number;
  h: number;
  rowGap: number;
  serpentine: boolean;
  effectiveReversed: boolean;
  override?: StringOverride;
  /** Conservative radius (meters) from the grid origin that contains every
   *  cell of this string — the scoped generator's envelope test. */
  envelopeM: number;
}

/**
 * Resolve a string entity's effective grid frame, or null when the layout
 * has no grid/transform. Every override field REPLACES its site default;
 * directionOverrideDeg replaces the entity rotation but the site-wide
 * directionOffsetDeg is still ADDED (historical semantics — see types.ts).
 */
export function stringGridFrame(
  layout: SiteLayout,
  entity: LayoutTextEntity,
): StringGridFrame | null {
  const config = layout.moduleGridConfig;
  if (!config || !layout.transform) return null;

  const coords = transformPoint(layout.transform, entity.cx, entity.cy);
  if (!coords) return null;
  const [lng, lat] = coords;

  const override = layout.stringOverrides?.[entity.id];
  const moduleCount = override?.moduleCount ?? config.moduleCount;
  const directionDeg =
    (override?.directionOverrideDeg ?? entity.rotation) + (config.directionOffsetDeg ?? 0);
  const polarityFlip = override?.polarityFlip ?? false;
  const effectiveReversed = (config.polarity === 'reversed') !== polarityFlip;
  const rows = override?.rows ?? config.rows;
  const w = override?.moduleWidth ?? config.moduleWidth;
  const g = override?.moduleGap ?? config.moduleGap;
  const h = override?.moduleHeight ?? config.moduleHeight;
  const rowGap = override?.rowGap ?? config.rowGap;
  const serpentine = override?.serpentine ?? config.serpentine;
  const modulesPerRow = Math.ceil(moduleCount / rows);

  const angleRad = (directionDeg * Math.PI) / 180;
  const offsetAlongM = override?.gridOffsetAlongM ?? config.gridOffsetAlongM ?? 0;
  const offsetPerpM = override?.gridOffsetPerpM ?? config.gridOffsetPerpM ?? 0;
  const offsetDx = offsetAlongM * Math.cos(angleRad) - offsetPerpM * Math.sin(angleRad);
  const offsetDy = offsetAlongM * Math.sin(angleRad) + offsetPerpM * Math.cos(angleRad);
  const { dLng: oDLng, dLat: oDLat } = metersToDeg(1, 1, lat);
  const gridLng = lng + offsetDx * oDLng;
  const gridLat = lat + offsetDy * oDLat;

  const halfAlong = (modulesPerRow * (w + g)) / 2 + w;
  const halfPerp = ((rows - 1) * (h + rowGap)) / 2 + h;
  let envelopeM = Math.hypot(halfAlong, halfPerp);
  if (override?.modulePositions) {
    // Dragged cells can sit outside the computed grid — EXPAND the envelope
    // to cover them rather than bypassing the test (at Hatzav 13% of strings
    // carry positions; a bypass would generate ~2,200 cells unconditionally
    // on every suggestion). Dragged counts are tiny, so this loop is free.
    const cellHalfDiag = Math.hypot(w, h) / 2;
    for (const pos of Object.values(override.modulePositions)) {
      const d = toLocalMeters(pos[0], pos[1], gridLng, gridLat);
      envelopeM = Math.max(envelopeM, Math.hypot(d.x, d.y) + cellHalfDiag);
    }
  }

  return {
    entity,
    gridLng,
    gridLat,
    directionDeg,
    moduleCount,
    rows,
    modulesPerRow,
    w,
    g,
    h,
    rowGap,
    serpentine,
    effectiveReversed,
    override,
    envelopeM,
  };
}

// (calculateModuleNumber deleted 2026-09-02: zero callers, disagreed with
// the GeoJSON path on module numbering, and ignored gridOffsetAlongM/PerpM
// entirely. Point-to-module resolution goes through the generated cells —
// suggest.ts point-in-polygon over generateModuleCellsNear.)

// ── Module grid GeoJSON generation ───────────────────────

/**
 * Generate GeoJSON FeatureCollection of module rectangles for map visualization.
 * Each feature is a polygon representing one module cell, with properties for
 * module number, string label, and entity ID.
 */
function appendCellsForFrame(frame: StringGridFrame, features: GeoJSON.Feature[]): void {
  const {
    entity, gridLng, gridLat, directionDeg, moduleCount, rows, modulesPerRow,
    w, g, h, rowGap, serpentine, effectiveReversed, override,
  } = frame;
  const angleRad = (directionDeg * Math.PI) / 180;

  for (let row = 0; row < rows; row++) {
    const modulesInThisRow = row < rows - 1
      ? modulesPerRow
      : moduleCount - modulesPerRow * (rows - 1);
    if (modulesInThisRow <= 0) continue;

    for (let col = 0; col < modulesInThisRow; col++) {
      // Position along axis (centered on label)
      const axisOffset = (col - modulesPerRow / 2 + 0.5) * (w + g);

      // Perpendicular offset — centered on label
      // Row 0 at positive perp, row N-1 at negative perp
      const perpOffset = rows > 1
        ? ((rows - 1) / 2 - row) * (h + rowGap)
        : 0;

      // Rotate offset by string direction
      const dx = axisOffset * Math.cos(angleRad) - perpOffset * Math.sin(angleRad);
      const dy = axisOffset * Math.sin(angleRad) + perpOffset * Math.cos(angleRad);

      // Convert meter offsets to degree offsets (from grid origin, not label)
      const { dLng, dLat } = metersToDeg(1, 1, gridLat);
      let centerLng = gridLng + dx * dLng;
      let centerLat = gridLat + dy * dLat;

      // Compute module number
      let effectiveCol = col + 1;
      if (rows > 1 && (row + 1) % 2 === 0 && serpentine) {
        effectiveCol = modulesInThisRow - col;
      }
      if (effectiveReversed) {
        effectiveCol = modulesInThisRow - effectiveCol + 1;
      }
      const moduleNumber = row * modulesPerRow + effectiveCol;

      // Apply per-module position override (if user dragged this module)
      const posOverride = override?.modulePositions?.[moduleNumber];
      if (posOverride) {
        centerLng = posOverride[0];
        centerLat = posOverride[1];
      }

      // Create rectangle polygon (unrotated, then rotate)
      const halfW = w / 2;
      const halfH = h / 2;

      // Rectangle corners in local meters relative to cell center
      const corners: [number, number][] = [
        [-halfW, -halfH],
        [halfW, -halfH],
        [halfW, halfH],
        [-halfW, halfH],
        [-halfW, -halfH],
      ];

      // Rotate and convert to lng/lat
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);
      const { dLng: cellDLng, dLat: cellDLat } = metersToDeg(1, 1, centerLat);
      const ring: [number, number][] = corners.map(([cx, cy]) => {
        const rx = cx * cos - cy * sin;
        const ry = cx * sin + cy * cos;
        return [
          centerLng + rx * cellDLng,
          centerLat + ry * cellDLat,
        ];
      });

      features.push({
        type: 'Feature',
        properties: {
          moduleNumber,
          totalModules: moduleCount,
          stringLabel: effectiveClassification(entity)?.value ?? entity.normalizedText,
          entityId: entity.id,
          row: row + 1,
          angleDeg: directionDeg,
          moduleWidth: w,
          moduleHeight: h,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [ring],
        },
      });
    }
  }
}

function stringEntitiesOf(layout: SiteLayout): LayoutTextEntity[] {
  return layout.textEntities.filter(
    (e) => effectiveClassification(e)?.level === 'string',
  );
}

export function generateModuleGridGeoJSON(
  layout: SiteLayout,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (!layout.moduleGridConfig || !layout.transform) {
    return { type: 'FeatureCollection', features };
  }
  for (const entity of stringEntitiesOf(layout)) {
    const frame = stringGridFrame(layout, entity);
    if (frame) appendCellsForFrame(frame, features);
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Cells NEAR a point: only strings whose grid envelope (which already covers
 * dragged module positions) can reach the point plus marginM are generated.
 * suggest.ts calls this on EVERY suggestion — the full-site generator
 * measured 0.8–1.6s at 26k–52k polygons, per click.
 *
 * marginM CONTRACT: for anything but a point query, marginM MUST be ≥ half
 * the query geometry's diagonal, or overlapping cells are silently dropped
 * and the caller reports "outside grid". Use generateModuleCellsNearGeometry
 * for geometry queries — it derives the margin for you.
 */
export function generateModuleCellsNear(
  layout: SiteLayout,
  lng: number,
  lat: number,
  marginM = 1,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (!layout.moduleGridConfig || !layout.transform) {
    return { type: 'FeatureCollection', features };
  }
  for (const entity of stringEntitiesOf(layout)) {
    const frame = stringGridFrame(layout, entity);
    if (!frame) continue;
    const d = toLocalMeters(lng, lat, frame.gridLng, frame.gridLat);
    if (Math.hypot(d.x, d.y) > frame.envelopeM + marginM) continue;
    appendCellsForFrame(frame, features);
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Cells near a GEOMETRY (e.g. an indication polygon for max-area-overlap
 * derives): margin = the geometry's bbox half-diagonal + 1m, so every string
 * whose cells the geometry could overlap is generated. A String OC marker
 * spans up to ~28m — a bare point-query margin would silently under-select.
 */
export function generateModuleCellsNearGeometry(
  layout: SiteLayout,
  geometry: GeoJSON.Geometry,
): GeoJSON.FeatureCollection {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const visit = (c: unknown): void => {
    if (Array.isArray(c)) {
      if (typeof c[0] === 'number' && typeof c[1] === 'number') {
        minLng = Math.min(minLng, c[0]); maxLng = Math.max(maxLng, c[0]);
        minLat = Math.min(minLat, c[1]); maxLat = Math.max(maxLat, c[1]);
      } else {
        for (const child of c) visit(child);
      }
    }
  };
  if (geometry.type === 'GeometryCollection') {
    for (const g of geometry.geometries) visit((g as { coordinates?: unknown }).coordinates);
  } else {
    visit((geometry as { coordinates?: unknown }).coordinates);
  }
  if (!isFinite(minLng)) return { type: 'FeatureCollection', features: [] };

  const cLng = (minLng + maxLng) / 2;
  const cLat = (minLat + maxLat) / 2;
  const d = toLocalMeters(maxLng, maxLat, cLng, cLat);
  return generateModuleCellsNear(layout, cLng, cLat, Math.hypot(d.x, d.y) + 1);
}

// ── Cell fit at a point (T-14: marker shape from the cell) ────────────

export interface CellFit {
  /** module cell size + gap + bearing from the string's resolved frame —
   *  per-string overrides respected (a site mixing portrait field and
   *  landscape canopy fits per cell, not per site) */
  w: number;
  h: number;
  g: number;
  directionDeg: number;
}

// id→entity per layout (mirrors deriveTopology's cache; layouts are
// replaced, not mutated, on change)
const fitEntityCache = new WeakMap<SiteLayout, Map<string, LayoutTextEntity>>();

function fitEntityById(layout: SiteLayout): Map<string, LayoutTextEntity> {
  let m = fitEntityCache.get(layout);
  if (!m) {
    m = new Map(layout.textEntities.map((t) => [t.id, t]));
    fitEntityCache.set(layout, m);
  }
  return m;
}

/**
 * The cell under (or nearest) a click, for MARKER SHAPE ONLY — size and
 * bearing, never topology (topology comes from the area derive on the
 * built rect; letting this decide fields would reintroduce point-based
 * answers on the add path). Never snaps position: while phantom grids /
 * stray rotations / overlapping cells exist, a marker sitting visibly off
 * its cell is the only remaining signal that the grid is wrong.
 *
 * Lookup is GENEROUS by design (c6 review of aabf4e7): production module
 * gaps are 0.02–0.06m, so a strict point-in-cell has a dead band between
 * every adjacent pair — two clicks centimeters apart would get visibly
 * different sizes AND bearings. A containing cell is preferred; otherwise
 * the nearest cell CENTER among the envelope-scoped candidates answers.
 * A generous SHAPE costs nothing (the derive re-runs its own contest on
 * the built rect); a strict one guarantees inconsistency. Clicks beyond
 * every grid envelope still return null (defaults apply).
 *
 * Ties resolve by distance, then entityId — deterministic.
 */
function frameAtPoint(
  layouts: SiteLayout[],
  lng: number,
  lat: number,
): { layout: SiteLayout; frame: StringGridFrame } | null {
  interface Cand { layout: SiteLayout; entityId: string; distM: number }
  const better = (a: Cand, b: Cand | null): boolean =>
    !b || a.distM < b.distM || (a.distM === b.distM && a.entityId < b.entityId);

  let bestInside: Cand | null = null;
  let bestNear: Cand | null = null;
  for (const layout of layouts) {
    if (!layout?.transform || !layout.moduleGridConfig) continue;
    const fc = generateModuleCellsNear(layout, lng, lat, 1);
    for (const f of fc.features) {
      if (f.geometry.type !== 'Polygon') continue;
      const ring = f.geometry.coordinates[0] as [number, number][];
      // local meters around the click (click = origin)
      let inside = false;
      const local = ring.slice(0, -1).map(([rlng, rlat]) => {
        const m = toLocalMeters(rlng, rlat, lng, lat);
        return [m.x, m.y] as [number, number];
      });
      const n = local.length;
      for (let i = 0, j = n - 1; i < n; j = i++) {
        const [xi, yi] = local[i];
        const [xj, yj] = local[j];
        if ((yi > 0) !== (yj > 0) && 0 < ((xj - xi) * (0 - yi)) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      let cx = 0, cy = 0;
      for (const [x, y] of local) { cx += x; cy += y; }
      const cand: Cand = {
        layout,
        entityId: f.properties!.entityId as string,
        distM: Math.hypot(cx / n, cy / n),
      };
      if (inside && better(cand, bestInside)) bestInside = cand;
      if (better(cand, bestNear)) bestNear = cand;
    }
  }
  const win = bestInside ?? bestNear;
  if (!win) return null;
  const entity = fitEntityById(win.layout).get(win.entityId);
  const frame = entity ? stringGridFrame(win.layout, entity) : null;
  return frame ? { layout: win.layout, frame } : null;
}

export function cellFitAtPoint(
  layouts: SiteLayout[],
  lng: number,
  lat: number,
): CellFit | null {
  const hit = frameAtPoint(layouts, lng, lat);
  if (!hit) return null;
  const { frame } = hit;
  return { w: frame.w, h: frame.h, g: frame.g, directionDeg: frame.directionDeg };
}

// ── String fit (String OC shape from the whole string) ───────────────

/**
 * The FULL footprint of one string as the grid draws it — the shape a
 * String OC marker should take. SHAPE ONLY, like CellFit: the caller keeps
 * the marker where the user put it (owner ruling 2026-09-14: ortho and grid
 * are never perfectly aligned; the marker fits the ortho, the grid only
 * names the string). `w` runs along the string axis, `h` across it.
 */
export interface StringFit {
  /** along-axis span: modulesPerRow × (module + gap) − gap, meters */
  w: number;
  /** cross-axis span: rows × module height + (rows − 1) × row gap, meters */
  h: number;
  directionDeg: number;
  entityId: string;
  layoutId: string;
  stringLabel: string;
  moduleCount: number;
  rows: number;
}

export function stringFitForFrame(layout: SiteLayout, frame: StringGridFrame): StringFit {
  const { modulesPerRow, rows, w, g, h, rowGap, directionDeg, moduleCount, entity } = frame;
  return {
    w: modulesPerRow * (w + g) - g,
    h: rows * h + (rows - 1) * rowGap,
    directionDeg,
    entityId: entity.id,
    layoutId: layout.id,
    stringLabel: effectiveClassification(entity)?.value ?? entity.normalizedText,
    moduleCount,
    rows,
  };
}

/** String footprint for the string whose cell sits under (or nearest) a
 *  point — same generous lookup as cellFitAtPoint. Null off every grid. */
export function stringFitAtPoint(
  layouts: SiteLayout[],
  lng: number,
  lat: number,
): StringFit | null {
  const hit = frameAtPoint(layouts, lng, lat);
  return hit ? stringFitForFrame(hit.layout, hit.frame) : null;
}

/** String footprint for a KNOWN string (e.g. the one the area derive named
 *  for an existing marker). Null when the layout/entity/grid is missing. */
export function stringFitForEntity(
  layouts: SiteLayout[],
  layoutId: string,
  entityId: string,
): StringFit | null {
  const layout = layouts.find((l) => l.id === layoutId);
  if (!layout) return null;
  const entity = fitEntityById(layout).get(entityId);
  const frame = entity ? stringGridFrame(layout, entity) : null;
  return frame ? stringFitForFrame(layout, frame) : null;
}

// ── Polarity auto-detection ──────────────────────────────

/**
 * Find "+" and "-" text items in the PDF entities and detect polarity.
 * Returns IDs of + and - entities, or null if none found.
 */
export function detectPolarityMarkers(
  layout: SiteLayout,
): { plusEntityIds: string[]; minusEntityIds: string[] } | null {
  if (!layout.transform) return null;

  const plusIds: string[] = [];
  const minusIds: string[] = [];

  for (const entity of layout.textEntities) {
    const text = entity.normalizedText.trim();
    if (text === '+') plusIds.push(entity.id);
    if (text === '-' || text === '−' || text === '–') minusIds.push(entity.id);
  }

  if (plusIds.length === 0 && minusIds.length === 0) return null;
  return { plusEntityIds: plusIds, minusEntityIds: minusIds };
}

/**
 * Suggest polarity setting based on detected + and - markers.
 * Returns 'normal' if + is at the start of string direction, 'reversed' otherwise.
 */
export function suggestPolarity(
  layout: SiteLayout,
): 'normal' | 'reversed' | null {
  if (!layout.transform || !layout.detectedPolarity) return null;

  const { plusEntityIds, minusEntityIds } = layout.detectedPolarity;
  if (plusEntityIds.length === 0 || minusEntityIds.length === 0) return null;

  // Find string entities
  const stringEntities = stringEntitiesOf(layout);
  if (stringEntities.length === 0) return null;

  // For each + marker, find nearest string entity and check which side it's on
  let normalVotes = 0;
  let reversedVotes = 0;

  const entityById = new Map(layout.textEntities.map((e) => [e.id, e]));

  for (const plusId of plusEntityIds) {
    const plusEntity = entityById.get(plusId);
    if (!plusEntity) continue;

    const plusCoords = transformPoint(layout.transform, plusEntity.cx, plusEntity.cy);
    if (!plusCoords) continue;

    // Find nearest string entity
    let nearestString: LayoutTextEntity | null = null;
    let nearestDist = Infinity;

    for (const strEntity of stringEntities) {
      const strCoords = transformPoint(layout.transform, strEntity.cx, strEntity.cy);
      if (!strCoords) continue;

      const dx = plusCoords[0] - strCoords[0];
      const dy = plusCoords[1] - strCoords[1];
      const dist = dx * dx + dy * dy;
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestString = strEntity;
      }
    }

    if (!nearestString) continue;

    const strCoords = transformPoint(layout.transform, nearestString.cx, nearestString.cy);
    if (!strCoords) continue;

    // Project + position onto string direction
    const angleRad = (nearestString.rotation * Math.PI) / 180;
    const local = toLocalMeters(plusCoords[0], plusCoords[1], strCoords[0], strCoords[1]);
    const projection = local.x * Math.cos(angleRad) + local.y * Math.sin(angleRad);

    // + at start of direction → normal polarity
    if (projection < 0) {
      normalVotes++;
    } else {
      reversedVotes++;
    }
  }

  if (normalVotes === 0 && reversedVotes === 0) return null;
  return normalVotes >= reversedVotes ? 'normal' : 'reversed';
}

// ── Direction detection ──────────────────────────────────

/**
 * Analyze string directions to detect dominant clusters. Returns ALL
 * clusters sorted by frequency (most common first) with their member entity
 * ids — minority clusters are usually label-rotation data bugs, and the
 * caller needs the ids to let the user select and fix them.
 *
 * Clusters the EFFECTIVE pre-offset bearing (directionOverrideDeg ?? label
 * rotation): a string already corrected via per-string override joins the
 * cluster its GRID actually uses. Clustering raw rotation flagged Hatzav's
 * five override-fixed strings as strays forever (owner caught it live —
 * "looks fine and the direction does not say 0 degrees").
 */
export function detectDominantDirections(
  layout: SiteLayout,
): Array<{ angleDeg: number; entityCount: number; entityIds: string[] }> {
  const stringEntities = stringEntitiesOf(layout);

  if (stringEntities.length === 0) return [];

  // Cluster normalized bearings [0, 360) by proximity (within 5 degrees)
  const clusters: Array<{ sum: number; count: number; ids: string[] }> = [];

  for (const e of stringEntities) {
    const bearing = layout.stringOverrides?.[e.id]?.directionOverrideDeg ?? e.rotation;
    const rot = ((bearing % 360) + 360) % 360;
    let merged = false;
    for (const cluster of clusters) {
      const avg = cluster.sum / cluster.count;
      const diff = Math.abs(rot - avg);
      const wrapDiff = 360 - diff;
      if (Math.min(diff, wrapDiff) <= 5) {
        cluster.sum += rot;
        cluster.count += 1;
        cluster.ids.push(e.id);
        merged = true;
        break;
      }
    }
    if (!merged) {
      clusters.push({ sum: rot, count: 1, ids: [e.id] });
    }
  }

  return clusters
    .map((c) => ({
      angleDeg: Math.round(c.sum / c.count),
      entityCount: c.count,
      entityIds: c.ids,
    }))
    .sort((a, b) => b.entityCount - a.entityCount);
}
